import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import SortableQuestion from "./SortableQuestion";
import { v4 as uuidv4 } from "uuid";

const TestForm = (props) => {
  const {
    questions,
    setQuestions,
    passPercentage,
    setPassPercentage,
    randomQuestions,
    setRandomQuestions,
    questionPool,
    setQuestionPool,
  } = props;
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    const activeRecord = questions.find((item) => item.id === active.id);
    const overRecord = questions.find((item) => item.id === over.id);

    if (active.id !== over.id) {
      setQuestions((items) => {
        const oldIndex = items.indexOf(activeRecord);
        const newIndex = items.indexOf(overRecord);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleNewQuestion = () => {
    const newQuestion = {
      content: "",
      id: uuidv4(),
    };
    setQuestions([...questions, newQuestion]); // Add new question with the new ID
    setSelectedQuestionId(newQuestion.id); // Set the new question as selected
  };

  return (
    <div className="flex flex-col">
      <div>
        <label className="block text-sm font-medium text-black mb-1">
          Procent zaliczenia {passPercentage}%
        </label>
        <input
          type="range"
          min="0"
          max="100"
          step="10"
          value={passPercentage}
          onChange={() => setPassPercentage(event.target.value)}
          className="w-full mt-1"
          style={{
            background: "linear-gradient(to right, #ef4444 0%, #22c55e 100%)",
            height: "4px",
            borderRadius: "2px",
            outline: "none",
            WebkitAppearance: "none",
          }}
        />
        <style jsx>{`
          input[type="range"]::-webkit-slider-thumb {
            -webkit-appearance: none;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #000;
            cursor: pointer;
          }
          input[type="range"]::-moz-range-thumb {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #000;
            cursor: pointer;
            border: none;
          }
          input[type="range"]::-ms-thumb {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #000;
            cursor: pointer;
            border: none;
          }
        `}</style>
      </div>

      <div className="flex gap-4 items-center mb-4 mt-1">
        <label className="block text-sm font-medium text-black mb-1">
          Pytania losowe
        </label>
        <input
          type="checkbox"
          className="w-full"
          checked={randomQuestions}
          onChange={() => setRandomQuestions(!randomQuestions)}
        />
      </div>

      <div className="flex gap-4 items-center mb-4 mt-1">
        <label className="block text-sm font-medium text-black mb-1">
          Pula pytań
        </label>
        <input
          type="number"
          className="rounded w-20 mt-1 border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-center"
          value={questionPool}
          max={questions.length}
          onChange={() => setQuestionPool(event.target.value)}
        />
      </div>

      <span>Treść pytania</span>

      <div className="border p-6 mt-5">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={questions}
            strategy={verticalListSortingStrategy}
          >
            {questions.map((question) => (
              <SortableQuestion
                key={question.id}
                id={question.id}
                question={question}
                setQuestions={setQuestions}
                setSelectedQuestionId={setSelectedQuestionId}
                selectedQuestionId={selectedQuestionId}
              />
            ))}
          </SortableContext>
        </DndContext>

        <button
          className="flex items-center gap-1 p-2 w-60 text-primaryYellow justify-center"
          onClick={() => handleNewQuestion()}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="size-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 4.5v15m7.5-7.5h-15"
            />
          </svg>
          Dodaj pytanie
        </button>
      </div>
    </div>
  );
};

TestForm.propTypes = {
  sectionId: PropTypes.string,
  lessonId: PropTypes.string,
  sections: PropTypes.array,
  setSections: PropTypes.func,
  setLessonId: PropTypes.func,
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      content: PropTypes.string.isRequired,
      answers: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          content: PropTypes.string.isRequired,
          correct: PropTypes.bool,
        }),
      ),
    }),
  ).isRequired,
  setQuestions: PropTypes.func.isRequired,
  passPercentage: PropTypes.number.isRequired,
  setPassPercentage: PropTypes.func.isRequired,
  randomQuestions: PropTypes.number.isRequired,
  setRandomQuestions: PropTypes.func.isRequired,
  questionPool: PropTypes.number.isRequired,
  setQuestionPool: PropTypes.func.isRequired,
};

export default TestForm;
